"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoggingInterceptor = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
const constants_1 = require("./constants");
const cross_fetch_1 = require("cross-fetch");
const sanitize_1 = require("./sanitize");
const LOG_LABEL = 'network';
const INVALID_URL = 'Invalid URL scheme';
class LoggingInterceptor {
    constructor({ logger, excludeUrls = [], printHeaderNames = constants_1.commonHeaders, sanitizeParameterNames = constants_1.unsafeQueryParams, meta = {}, }) {
        this.context = { url: undefined };
        this.request = (...args) => {
            this.context.requestStartTime = Date.now();
            try {
                const [info, init] = args;
                this.context.logMeta = Object.assign(Object.assign({}, this.context.logMeta), init === null || init === void 0 ? void 0 : init.logMeta);
                const req = typeof info === 'string' ? new cross_fetch_1.Request(info, init) : info;
                // see https://developer.mozilla.org/en-US/docs/Web/API/Request
                this.context.url = req.url;
                this.context.method = req.method;
                this.context.headers = req.headers;
                this.context.shouldLog = !this.excludeUrls.some((excludeUrl) => this.context.url.indexOf(excludeUrl) !== -1);
                this.parseUrl();
                if (this.context.shouldLog) {
                    this.logger.info(this.getRequestInfo());
                }
            }
            catch (e) {
                /* istanbul ignore else */
                if (e.message === INVALID_URL) {
                    throw e;
                }
            }
            return args;
        };
        this.response = (resp) => {
            try {
                if (this.context.shouldLog) {
                    this.logger.info(this.getResponseInfo(resp));
                }
            }
            catch (e) { }
            return resp;
        };
        this.error = (error) => {
            try {
                /* istanbul ignore else */
                if (this.context.shouldLog) {
                    const meta = this.getRequestInfo();
                    if (error.message === INVALID_URL) {
                        meta.url = this.context.url;
                    }
                    meta.error = error;
                    meta.event = 'ERROR';
                    meta.duration = this.getResponseTime();
                    this.logger.error(meta);
                }
            }
            catch (e) { }
            throw error;
        };
        this.logger = logger;
        this.excludeUrls = excludeUrls;
        this.commonHeaders = new Set([...printHeaderNames].map((a) => a.toLowerCase()));
        this.sanitizeParameterNames = new Set(sanitizeParameterNames);
        this.context.logMeta = Object.assign({}, meta);
    }
    parseUrl() {
        // fast fail if URL is not starting with http or https
        if (!/^http(s)?:/.test(this.context.url)) {
            throw new Error(INVALID_URL);
        }
        // fail if URL can't be parsed
        try {
            this.context.parsedUrl = new URL(this.context.url);
        }
        catch (e) {
            throw new Error(INVALID_URL);
        }
        const { searchParams } = this.context.parsedUrl;
        const query = {};
        for (const [key] of searchParams) {
            const value = searchParams.getAll(key);
            query[key] = value.length > 1 ? value : value[0];
        }
        this.context.parsedUrl.query = sanitize_1.sanitizeParameters(query, this.sanitizeParameterNames);
        this.context.parsedUrl.pathname = sanitize_1.sanitizePath(this.context.parsedUrl.pathname);
    }
    headerToObject(header) {
        const headerObj = {};
        const keys = header.keys();
        for (const key of keys) {
            if (this.commonHeaders.has(key) && header.has(key)) {
                headerObj[key] = header.get(key);
            }
        }
        return headerObj;
    }
    getResponseTime() {
        return Date.now() - this.context.requestStartTime;
    }
    getRequestInfo() {
        var _a, _b, _c, _d;
        const headers = this.context.headers;
        return Object.assign({ label: LOG_LABEL, event: 'REQUEST', method: this.context.method, protocol: (_a = this.context.parsedUrl) === null || _a === void 0 ? void 0 : _a.protocol, dest: (_b = this.context.parsedUrl) === null || _b === void 0 ? void 0 : _b.hostname, path: (_c = this.context.parsedUrl) === null || _c === void 0 ? void 0 : _c.pathname, headers: this.headerToObject(headers), query: (_d = this.context.parsedUrl) === null || _d === void 0 ? void 0 : _d.query }, this.context.logMeta);
    }
    getResponseInfo(resp) {
        const headers = resp.headers;
        return Object.assign({ label: LOG_LABEL, event: 'RESPONSE', method: this.context.method, protocol: this.context.parsedUrl.protocol, dest: this.context.parsedUrl.hostname, path: this.context.parsedUrl.pathname, headers: this.headerToObject(headers), query: this.context.parsedUrl.query, status: resp.status, duration: this.getResponseTime() }, this.context.logMeta);
    }
}
exports.LoggingInterceptor = LoggingInterceptor;
